import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import apiClient from '../../api/client';

// Editing a member — identity, role, standing.
//
// The Users tab could add and remove people but never change one, so a
// promotion meant delete-and-reinvite, and most people sensibly did
// neither. The API had `PUT /api/user/:accountId` the whole time; no
// screen called it.
//
// Which roles the select offers is decided by the same rule the server
// enforces (utils/roles.js in the backend): you can grant what you could
// act as, you cannot touch somebody above you, a BC changes nobody's
// reach, and SA is not on the menu for anyone — platform accounts come
// from the invitation flow. The copy here is a courtesy; the server is
// what refuses.

// AO is the owner since 2026-10-24 (it was CA).
const RANK: Record<string, number> = { AO: 3, CA: 2, BC: 1, Driver: 0, Maintainer: 0 };
const COMPANY_ROLES = ['AO', 'CA', 'BC', 'Driver', 'Maintainer'];

export function assignableRoles(callerRole: string, currentRole: string): string[] {
  if (currentRole === 'SA') return [];
  if (callerRole === 'SA') return COMPANY_ROLES;
  const callerRank = RANK[callerRole] ?? -1;
  if (callerRank < RANK.CA) return [];
  if ((RANK[currentRole] ?? Infinity) > callerRank) return [];
  return COMPANY_ROLES.filter((r) => RANK[r] <= callerRank);
}

interface Row {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  status: string;
}

const EditUserDialog: React.FC<{
  open: boolean;
  user: Row | null;
  callerRole: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}> = ({ open, user, callerRole, onClose, onSaved }) => {
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', role: '', status: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && user) {
      setForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        role: user.role,
        status: user.status,
      });
      setError(null);
    }
  }, [open, user]);

  if (!user) return null;

  const roles = assignableRoles(callerRole, user.role);
  // Offer the current role even when it is not grantable, so the select
  // is never showing a value its own menu denies exists.
  const roleOptions = roles.includes(user.role) ? roles : [user.role, ...roles];
  const canEditRole = roles.length > 0;

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    // Send only what changed. The endpoint treats every present field as
    // an instruction, and a role it was not asked to change should not
    // appear in the audit trail as one that was.
    const baseline: Record<string, string> = {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
      role: user.role,
      status: user.status,
    };
    const changes: Record<string, string> = {};
    (['firstName', 'lastName', 'phone', 'role', 'status'] as const).forEach((k) => {
      if (form[k] !== baseline[k]) changes[k] = form[k];
    });
    if (!Object.keys(changes).length) {
      setBusy(false);
      onClose();
      return;
    }
    try {
      await apiClient.put(`/api/user/${user._id}`, changes);
      onSaved(
        changes.role
          ? `${form.firstName || user.email} is now ${changes.role}.`
          : 'Saved.',
      );
      onClose();
    } catch (err: any) {
      const body = err?.response?.data;
      setError(body?.message || body?.error || 'That change was not saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{`${user.firstName} ${user.lastName}`.trim() || user.email}</DialogTitle>
      <DialogContent>
        <Stack gap={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" gap={1.5}>
            <TextField label="First name" size="small" fullWidth value={form.firstName} onChange={set('firstName')} />
            <TextField label="Last name" size="small" fullWidth value={form.lastName} onChange={set('lastName')} />
          </Stack>
          <TextField label="Phone" size="small" fullWidth value={form.phone} onChange={set('phone')} />

          <TextField
            label="Role"
            size="small"
            select
            fullWidth
            value={form.role}
            onChange={set('role')}
            disabled={!canEditRole}
            helperText={canEditRole ? undefined : 'Your role cannot change this one.'}
          >
            {roleOptions.map((r) => (
              <MenuItem key={r} value={r} disabled={!roles.includes(r)}>{r}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Status"
            size="small"
            select
            fullWidth
            value={form.status}
            onChange={set('status')}
          >
            {/* `pending` stays selectable only as the value it already is —
                you leave pending by activating, never by choosing it. */}
            {user.status === 'pending' && <MenuItem value="pending" disabled>pending</MenuItem>}
            <MenuItem value="active">active</MenuItem>
            <MenuItem value="suspended">suspended</MenuItem>
            {/* Left the company: kept for payroll and records, no login,
                no chats, no paperwork reminders. */}
            <MenuItem value="former">former (left the company)</MenuItem>
          </TextField>

          {user.status === 'pending' && form.status === 'active' && (
            <Typography variant="caption" color="text.secondary">
              Activating works only if this person has already set a password.
              Otherwise resend their invitation from the list instead.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;
